import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import { contactApi } from "../../../assets/apis";
import { selectStyle } from "../../../assets/selectCustomStyle";
import { useDispatch, useSelector } from "react-redux";
import ConfirmPortal from "../../../uiModals/confirmModel/ConfirmPortal";
import {
  setSiderBarActiveLink,
  setSiderBarActiveList,
} from "../../../redux-store/sidebarActiveList";
import { hasPermission } from "../../../utils/helpers";

const Contacts = () => {
  const { FYear } = useSelector((store) => store.FinancialYear);
  const { loggedUser } = useSelector((store) => store.Auth);
  const dispatch = useDispatch();

  const [openPortal, setOpenPortal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const [queryParams, setQueryParams] = useState({ page: 1, pageSize: 10 });

  const navigate = useNavigate();

  const deleteContact = async (id) => {
    handleClosePortal();
    try {
      await axios.delete(`${contactApi}/${id}`);
      toast.success("Contact deleted successfully");
      refetch();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    const errorMessage = error.response?.data?.message || "An error occurred";
    toast.error(errorMessage);
  };

  const onChangeFilterHandler = (e) => {
    const { name, value } = e.target;
    setFilters((pre) => ({ ...pre, [name]: value }));
  };

  const handleInputChangeWithCondition = (e, validationRegex) => {
    const { name, value } = e.target;
    if (value === "" || validationRegex.test(value)) {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onSearchHandler = (e) => {
    e.preventDefault();
    setQueryParams((pre) => ({ ...pre, ...filters, ...pagination }));
  };

  const onResetHandler = () => {
    setFilters({});
    setQueryParams((pre) => ({ page: 1, pageSize: 10 }));
    setPagination({ page: 1, pageSize: 10 });
  };

  // Function to close the modal
  const handleOpenPortal = (data) => {
    setDeletingItem(data);
    setOpenPortal(true);
  };
  const handleClosePortal = () => {
    setDeletingItem(null);
    setOpenPortal(false);
  };

  const fetchContacts = async ({ queryKey }) => {
    const [, queryParamsData] = queryKey;
    const queryParams = new URLSearchParams(queryParamsData);
    try {
      const response = await axios.get(`${contactApi}?${queryParams}`);
      const { totalPages, contacts } = response.data;
      setAllContacts(contacts);
      setTotalPages(totalPages);
      return contacts;
    } catch (error) {
      console.error(error);
      // Handle the error, perhaps set an error state or throw it further
      handleApiError(error);
    }
  };

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["contacts", queryParams],
    queryFn: fetchContacts,
  });

  useEffect(() => {
    setQueryParams((pre) => ({ ...pre, ...pagination }));
  }, [pagination.page, pagination.pageSize, pagination]);

  useEffect(() => {
    refetch();
  }, [FYear.value]);

  useEffect(() => {
    dispatch(setSiderBarActiveList({ name: "Contacts" }));
    dispatch(setSiderBarActiveLink({ link: "contacts" }));
  }, []);

  return (
    <div className="main-panel  ">
      <div className="content-wrapper">
        <div id="first" className="card col-lg-12 p-3">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="card-title">
              {" "}
              All Contacts{" "}
              <i
                className="fa fa-info-circle text-secondary hover-cursor"
                data-toggle="tooltip"
                title="Contact who has already become our client."
                aria-hidden="true"
                data-original-title="Contact who has already become our client."
              ></i>
            </div>
            {hasPermission(loggedUser, 1) && (
              <button
                className="btn btn-primary d-flex justify-content-center align-items-center px-2 py-1"
                onClick={() => navigate("/add/contact")}
              >
                {/* <i className="mdi  mdi-account-plus me-1"></i> */}
                Add Contact
              </button>
            )}
          </div>
          <form className="row" onSubmit={onSearchHandler}>
            <div className="col-lg-2">
              <div className="form-group">
                <label className="form-control-label" htmlFor="name">
                  Name
                </label>
                <input
                  type="text"
                  className="form-control "
                  name="name"
                  id="name"
                  value={filters.name || ""}
                  onChange={(e) =>
                    handleInputChangeWithCondition(e, /^[\s\S]*$/)
                  }
                />
              </div>
            </div>
            <div className="col-lg-2">
              <div className="form-group">
                <label className="form-control-label" htmlFor="phone">
                  Mobile Number
                </label>
                <input
                  type="text"
                  className="form-control "
                  name="phone"
                  id="phone"
                  value={filters.phone || ""}
                  minLength={10}
                  maxLength={10}
                  onChange={(e) =>
                    handleInputChangeWithCondition(e, /^[0-9\b]+$/)
                  }
                />
              </div>
            </div>
            <div className="col-lg-2">
              <div className="form-group">
                <label className="form-control-label" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  className="form-control "
                  name="email"
                  id="email"
                  value={filters.email || ""}
                  onChange={onChangeFilterHandler}
                />
              </div>
            </div>
            <div className="col-lg-2">
              <div className="form-group">
                <label className="form-control-label" htmlFor="billing_city">
                  City
                </label>
                <input
                  type="text"
                  className="form-control "
                  name="billing_city"
                  id="billing_city"
                  value={filters.billing_city || ""}
                  onChange={onChangeFilterHandler}
                />
              </div>
            </div>
            <div className="col-lg-2">
              <div className="form-group">
                <label className="form-control-label" htmlFor="contactType">
                  Contact Type
                </label>
                <select
                  id="contactType"
                  name="contactType"
                  className="form-control "
                  value={filters.contactType || ""}
                  onChange={onChangeFilterHandler}
                  style={selectStyle}
                >
                  <option value="">-&nbsp;-select-&nbsp;-</option>
                  <option value="customer">Customer</option>
                  <option value="supplier">Supplier</option>
                </select>
              </div>
            </div>

            <div className="col-lg-2 text-right">
              <div
                className="form-group mt-1"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="submit"
                  id="search"
                  className="btn btn-primary btn-sm mt-4"
                >
                  Search
                </button>
                <button
                  type="button"
                  id="reset"
                  className="btn btn-primary btn-sm mt-4"
                  onClick={onResetHandler}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>

          <div className="d-flex flex-column  align-items-center datatable">
            <DataTable
              value={allContacts}
              style={{ width: "100%" }}
              id="review-table"
              className="mytable"
            >
              <Column
                className="border-bottom border-top "
                header="SR. No."
                body={(rowData, { rowIndex }) =>
                  pagination.page * pagination.pageSize -
                  pagination.pageSize +
                  rowIndex +
                  1
                }
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "4rem",
                }}
              />

              <Column
                className="border-bottom border-top"
                field="name"
                header="NAME"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "7rem",
                  padding: "0 5px",
                }}
              />

              <Column
                className="border-bottom border-top"
                field="phone"
                header="PHONE"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "5rem",
                  padding: "0 5px",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="email"
                header="EMAIL"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "5rem",
                  padding: "0 5px",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="billing_state"
                header="STATE"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "7rem",
                  padding: "0 5px",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="billing_country"
                header="COUNTRY"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "7rem",
                  padding: "0 5px",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="contactType"
                header="TYPE"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "5rem",
                  padding: "0 5px",
                }}
              />

              <Column
                className="border-bottom border-top"
                body={(data) => (
                  <div
                    style={{
                      minWidth: "50%",
                      maxWidth: "2rem",
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    {hasPermission(loggedUser, 2) && (
                      <i
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/edit/contact/${data.id}`)}
                        className="dropdown-item-icon mdi mdi-pen text-primary me-2"
                      />
                    )}
                    {hasPermission(loggedUser, 3) && (
                      <i
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          handleOpenPortal(data);
                        }}
                        className="dropdown-item-icon mdi mdi-delete-forever text-primary me-2"
                      />
                    )}
                  </div>
                )}
                header="ACTIONS"
              />
            </DataTable>
            <div
              className=""
              style={{
                display: "flex",
                width: "80%",
                justifyContent: "space-evenly",
                margin: "10px 0 0 0",
              }}
            >
              <select
                value={pagination.pageSize}
                onChange={(e) =>
                  setPagination((prev) => {
                    return { ...prev, pageSize: e.target.value };
                  })
                }
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
              <button
                style={{ border: "none", fontSize: "24px" }}
                disabled={pagination.page === 1}
                onClick={() =>
                  setPagination((prev) => {
                    return { ...prev, page: prev.page - 1 };
                  })
                }
              >
                <i className="mdi mdi-chevron-left" />
              </button>
              <span>
                Page {pagination.page} of {totalPages}
              </span>
              <button
                style={{ border: "none", fontSize: "24px" }}
                disabled={pagination.page === totalPages}
                onClick={() =>
                  setPagination((prev) => {
                    return { ...prev, page: prev.page + 1 };
                  })
                }
              >
                <i className="mdi mdi-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {openPortal && (
        <ConfirmPortal
          onClose={handleClosePortal}
          type={"Customer"}
          name={deletingItem.name}
          id={deletingItem.id}
          handleDalete={deleteContact}
        ></ConfirmPortal>
      )}
    </div>
  );
};

export default Contacts;
